import debounce from 'lodash.debounce'

import { CancelablePromise, apiRequest } from '../../../main/services/api'
import { getDate } from '../../header/utils'
import Treemap from '../treemap'
import { MapData, MapDataNode, MapTypeId, PerfData, Scale } from '../types'
import { getVisibleTooltipTickers } from '../utils'
import AppDispatcher from './appDispatcher'
import { ActionTypes } from './constants'
import mapStore from './mapStore'

var _targetScale = 1
function zoomIn(treemap: Treemap, scale: number, tx: number, ty: number) {
  _targetScale = scale
  animate()
  function animate() {
    treemap.zoom.scale(+(treemap.zoom.scale() + 0.05).toFixed(2), { x: tx, y: ty })
    AppDispatcher.handleViewAction({ type: ActionTypes.RENDER_TREEMAP })
    if (treemap.zoom.scale() + 0.05 < _targetScale + 0.00001) {
      requestAnimationFrame(animate)
    }
  }
}

function zoomOut(treemap: Treemap, scale: number, tx: number, ty: number) {
  _targetScale = scale
  animate()
  function animate() {
    treemap.zoom.scale(+(treemap.zoom.scale() - 0.05).toFixed(2), { x: tx, y: ty })
    AppDispatcher.handleViewAction({ type: ActionTypes.RENDER_TREEMAP })
    if (treemap.zoom.scale() - 0.05 > _targetScale - 0.00001) {
      requestAnimationFrame(animate)
    }
  }
}

function updateVisibleSparklines(treemap: Treemap) {
  const hoveredNode = mapStore.getHoveredNode()
  if (hoveredNode) {
    const tickersToLoad = getVisibleTooltipTickers(hoveredNode)

    if (!tickersToLoad.includes(hoveredNode.name)) {
      tickersToLoad.push(hoveredNode.name)
    }

    if (tickersToLoad.length) {
      debouncedLoadSparklines(tickersToLoad, treemap.type)
    }
  }
}

let _lastSparklinesRequest: CancelablePromise<any> | null = null
async function loadSparklines(tickers: string[], mapType: string) {
  AppDispatcher.handleViewAction({
    type: ActionTypes.LOAD_SPARKLINES_STARTED,
  })
  _lastSparklinesRequest?.cancel?.()
  _lastSparklinesRequest = apiRequest(
    '/api/map_sparklines.ashx',
    { query: { t: tickers.join(','), ty: mapType } },
    true
  )

  try {
    const data = await _lastSparklinesRequest
    AppDispatcher.handleServerAction({
      type: ActionTypes.LOAD_SPARKLINES_COMPLETED,
      data: data,
    })
  } catch (error) {
    AppDispatcher.handleServerAction({
      type: ActionTypes.LOAD_SPARKLINES_FAILED,
      error,
    })
  }
}

const debouncedLoadSparklines = debounce(loadSparklines, 200, { trailing: true })

const actions = {
  setHoveredNode(node: MapDataNode | undefined) {
    if (!node) {
      AppDispatcher.handleViewAction({
        type: ActionTypes.RESET_SPARKLINE_DATA,
      })
    }

    AppDispatcher.handleViewAction({
      type: ActionTypes.SET_HOVERED_NODE,
      node,
    })
  },

  loadSparklineData(tickers: string[], mapType: MapTypeId) {
    if (tickers) {
      debouncedLoadSparklines(tickers, mapType)
    }
  },

  loadSparklinesCancel() {
    _lastSparklinesRequest?.cancel?.()
  },

  resetSparklineData() {
    AppDispatcher.handleViewAction({
      type: ActionTypes.RESET_SPARKLINE_DATA,
    })
  },

  changeTranslate(treemap: Treemap, tx: number, ty: number) {
    treemap.zoom.translate([tx, ty])
    AppDispatcher.handleViewAction({
      type: ActionTypes.CHANGE_TRANSLATE,
    })
  },

  zoom(treemap: Treemap, direction: number, offsetX: number, offsetY: number) {
    if (direction > 0) {
      const zoomLevel = treemap.getNextZoomLevel()
      if (zoomLevel !== treemap.zoom.scale()) {
        zoomIn(treemap, zoomLevel, offsetX, offsetY)
      }
    } else {
      const zoomLevel = treemap.getPreviousZoomLevel()
      if (zoomLevel !== treemap.zoom.scale()) {
        zoomOut(treemap, zoomLevel, offsetX, offsetY)
      }
    }
  },

  render() {
    AppDispatcher.handleViewAction({ type: ActionTypes.RENDER_TREEMAP })
  },

  zoomAndTranslate(treemap: Treemap, scale: number, tx: number, ty: number) {
    treemap.zoom.scale(1, { x: tx, y: ty })
    treemap.zoom.scale(scale, { x: tx, y: ty })
    AppDispatcher.handleViewAction({ type: ActionTypes.RENDER_TREEMAP })
  },

  updateLayout(treemap: Treemap, props: { width: number; height: number; data: MapData; scale: Scale }) {
    treemap.updateData(props)
    treemap.zoom.updateDimensions(props)
    AppDispatcher.handleViewAction({ type: ActionTypes.RENDER_TREEMAP })

    // Update hovered node if any
    const hoveredNode = mapStore.getHoveredNode()
    if (hoveredNode) {
      AppDispatcher.handleViewAction({
        type: ActionTypes.SET_HOVERED_NODE,
        node: treemap.nodes.find((node) => node.name === hoveredNode.name),
      })
      updateVisibleSparklines(treemap)
    }
  },

  updatePerfData(treemap?: Treemap, data?: PerfData) {
    if (!treemap || !data) return

    treemap.updatePerf(data)
    AppDispatcher.handleServerAction({
      type: ActionTypes.UPDATE_DATA,
      version: data.version,
      timestamp: getDate(),
    })

    updateVisibleSparklines(treemap)
  },

  _setOnPublish(getPublishCanvas: () => HTMLCanvasElement) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.SET_PUBLISH_CANVAS,
      getPublishCanvas,
    })
  },

  setupWidget() {
    AppDispatcher.handleViewAction({
      type: ActionTypes.SET_WIDGET,
      isWidget: true,
    })
  },
}

export default actions
